/**
 * Copyright (C) 2026 alaraajavamma <aki@urheiluaki.fi>
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program.  If not, see <https://www.gnu.org/licenses/>.
 *
 * SPDX-License-Identifier: GPL-3.0-or-later
 */

import React from 'react';

interface IntersportLogoProps {
  className?: string;
  variant?: 'color' | 'mono' | 'white';
  showText?: boolean;
  symbolOnly?: boolean;
  height?: string; // Custom Tailwind height e.g. "h-12" or "h-[64px]"
  symbolSize?: number; // Size in pixels of the clipped square symbol
}

export const IntersportLogo: React.FC<IntersportLogoProps> = ({
  className = '',
  variant = 'color',
  showText = false,
  symbolOnly = false,
  height,
  symbolSize,
}) => {
  // Official corporate colors
  const grayColor = variant === 'color' ? '#555555' : variant === 'white' ? 'rgba(255, 255, 255, 0.9)' : '#000000';

  // CSS filters to map the official colored SVG to pure white or pure black monochrome
  const getFilterStyle = () => {
    if (variant === 'white') {
      return { filter: 'brightness(0) invert(1)' };
    }
    if (variant === 'mono') {
      return { filter: 'brightness(0)' };
    }
    return {};
  };

  if (symbolOnly) {
    const size = symbolSize || 32;
    return (
      <div className={`overflow-hidden relative select-none shrink-0 ${className}`} style={{ width: `${size}px`, height: `${size}px` }}>
        <img
          src="https://upload.wikimedia.org/wikipedia/commons/8/8e/Intersport_logo.svg"
          alt="Intersport Symbol"
          className="absolute left-0 top-0 max-w-none block object-contain shrink-0"
          style={{ height: `${size}px`, ...getFilterStyle() }}
          referrerPolicy="no-referrer"
        />
      </div>
    );
  }

  return (
    <div className={`flex flex-col items-center select-none ${className}`}>
      {/* Official Intersport SVG Mark containing the iconic shape & wordmark */}
      <img
        src="https://upload.wikimedia.org/wikipedia/commons/8/8e/Intersport_logo.svg"
        alt="Intersport"
        className={`${height || 'h-[32px]'} w-auto max-w-full block object-contain shrink-0`}
        style={getFilterStyle()}
        referrerPolicy="no-referrer"
      />

      {/* "FORSSA" text perfectly positioned beneath the main logo */}
      {showText && (
        <div 
          className="text-[9px] sm:text-[10px] font-bold uppercase tracking-[0.41em] font-sans mt-1 text-center translate-x-[12px]"
          style={{ 
            color: grayColor,
          }}
        >
          FORSSA
        </div>
      )}
    </div>
  );
};
